import { useCallback } from 'react';

// 해시태그 매치 타입 정의
export interface HashtagMatch {
    index: number;
    matchText: string;
    length: number;
}

/**
 * 텍스트에서 해시태그를 찾는 함수
 * 단일 공백은 해시태그 내부로 포함하고, 연속된 두 개의 공백(스페이스바 두 번)으로 해시태그의 끝을 구분
 */
export const useFindHashtags = () => {
    return useCallback((text: string): HashtagMatch[] => {
        // 해시태그 시작과 끝을 찾는 로직
        const matches: HashtagMatch[] = [];
        
        // 입력 텍스트가 없으면 빈 배열 반환
        if (!text || text.trim() === '') {
            console.log('[useFindHashtags] 입력 텍스트 없음');
            return matches;
        }

        const regex = /#[^\s#][^#]*?(?=\s{2}|$)/g;
        let match: RegExpExecArray | null;
        
        while ((match = regex.exec(text)) !== null) {
            const hashtagText = match[0];
            const startIndex = match.index;
            
            // 해시태그가 빈 문자열이 아닌지 확인
            if (hashtagText.length > 1) {
                // 해시태그 끝에 있는 단일 공백은 제외 (연속된 공백의 첫 번째)
                let cleanHashtagText = hashtagText;
                if (cleanHashtagText.endsWith(' ') && 
                    startIndex + cleanHashtagText.length < text.length && 
                    text[startIndex + cleanHashtagText.length] === ' ') {
                    cleanHashtagText = cleanHashtagText.slice(0, -1);
                    console.log(`[useFindHashtags] 해시태그 끝 공백 제거: '${hashtagText}' -> '${cleanHashtagText}'`);
                }
                
                console.log(`[useFindHashtags] 찾은 해시태그: '${cleanHashtagText}' (위치: ${startIndex})`);
                
                matches.push({
                    index: startIndex,
                    matchText: cleanHashtagText,
                    length: cleanHashtagText.length
                });
            }
        }
        
        console.log('[useFindHashtags] 찾은 해시태그 수:', matches.length);
        return matches;
    }, []);
};

/**
 * 텍스트의 커서 위치를 저장하고 복원하는 훅
 */
export const useSelectionManagement = (inputRef: React.RefObject<HTMLDivElement>) => {
    // 커서 위치 저장
    const saveSelection = useCallback(() => {
        if (!inputRef.current) return null;
        
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return null;
        
        const range = selection.getRangeAt(0);
        const preSelectionRange = range.cloneRange();
        preSelectionRange.selectNodeContents(inputRef.current);
        preSelectionRange.setEnd(range.startContainer, range.startOffset);
        
        return {
            start: preSelectionRange.toString().length,
            end: preSelectionRange.toString().length + range.toString().length
        };
    }, [inputRef]);
    
    // 커서 위치 복원
    const restoreSelection = useCallback((savedSel: { start: number; end: number }) => {
        if (!inputRef.current) return;
        
        const selection = window.getSelection();
        if (!selection) return;
        
        let charIndex = 0;
        const range = document.createRange();
        range.setStart(inputRef.current, 0);
        range.collapse(true);
        
        const nodeStack: Node[] = [inputRef.current];
        let node: Node | null;
        let foundStart = false;
        let foundEnd = false;
        
        while (!foundEnd && (node = nodeStack.pop())) {
            if (node && node.nodeType === Node.TEXT_NODE) {
                const nextCharIndex = charIndex + (node.textContent?.length || 0);
                
                if (!foundStart && savedSel.start >= charIndex && savedSel.start <= nextCharIndex) {
                    range.setStart(node, savedSel.start - charIndex);
                    foundStart = true;
                }
                
                if (foundStart && savedSel.end >= charIndex && savedSel.end <= nextCharIndex) {
                    range.setEnd(node, savedSel.end - charIndex);
                    foundEnd = true;
                }
                
                charIndex = nextCharIndex;
            } else if (node) {
                let i = node.childNodes.length;
                while (i--) {
                    nodeStack.push(node.childNodes[i]);
                }
            }
        }
        
        selection.removeAllRanges();
        selection.addRange(range);
    }, [inputRef]);
    
    return { saveSelection, restoreSelection };
};

/**
 * 해시태그 스타일링을 적용하는 훅
 */
export const useHashtagStyling = (inputRef: React.RefObject<HTMLDivElement>) => {
    const findHashtags = useFindHashtags();
    const { saveSelection, restoreSelection } = useSelectionManagement(inputRef);
    
    const applyHashtagStyling = useCallback(() => {
        if (!inputRef.current) return;
        
        // 텍스트 컨텐츠 가져오기
        const text = inputRef.current.textContent || '';
        // console.log('[useHashtagStyling] 처리할 텍스트:', JSON.stringify(text));
        
        // 해시태그 찾기
        const hashtagMatches = findHashtags(text);
        // console.log('[useHashtagStyling] 검출된 해시태그:', hashtagMatches);
        
        // 해시태그가 없으면 처리하지 않음
        if (hashtagMatches.length === 0) {
            // console.log('[useHashtagStyling] 해시태그 없음, 스타일링 건너뜀');
            return;
        }
        
        // 현재 선택 상태 저장
        const savedSelection = saveSelection();
        
        // 텍스트를 순회하며 해시태그 감지 및 HTML 적용
        let lastIndex = 0;
        let html = '';
        
        for (const match of hashtagMatches) {
            // 해시태그 앞의 일반 텍스트 추가
            const beforeText = text.substring(lastIndex, match.index);
            html += beforeText;
            console.log(`[useHashtagStyling] 해시태그 전 텍스트 추가: '${beforeText}'`);
            
            // 해시태그 추가 (스타일 적용)
            const hashtagText = match.matchText;
            console.log(`[useHashtagStyling] 스타일링할 해시태그: '${hashtagText}'`);
            html += `<span class="hashtag" data-hashtag="true">${hashtagText}</span>`;
            
            // 다음 인덱스 설정
            lastIndex = match.index + match.length;
            
            // 연속된 공백 처리: 해시태그 직후에 연속된 공백이 있는지 확인
            let consecutiveSpaces = '';
            let i = lastIndex;
            while (i < text.length && text[i] === ' ') {
                consecutiveSpaces += ' ';
                i++;
            }
            
            if (consecutiveSpaces.length >= 2) {
                console.log(`[useHashtagStyling] 연속된 공백 발견: ${consecutiveSpaces.length}개`);
                html += consecutiveSpaces;
                lastIndex = i;
            }
        }
        
        // 남은 텍스트 추가
        const remainingText = text.substring(lastIndex);
        html += remainingText;
        console.log(`[useHashtagStyling] 남은 텍스트 추가: '${remainingText}'`);
        
        console.log('[useHashtagStyling] 최종 HTML:', html);
        
        // 항상 HTML 업데이트 (조건 없이)
        inputRef.current.innerHTML = html;
        
        // 선택 상태 복원
        if (savedSelection) {
            restoreSelection(savedSelection);
        }
    }, [findHashtags, saveSelection, restoreSelection, inputRef]);
    
    return { applyHashtagStyling };
};

export default { useFindHashtags, useSelectionManagement, useHashtagStyling }; 